import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import ManutenzioneDb from "@/pages/ManutenzioneDb";
import { StrutturaMenu } from "@/components/StrutturaMenu";
import { PushEventiAdmin } from "@/components/PushEventiAdmin";
import { inputCls } from "@/components/Filters";
import { dateIt, MESI, num } from "@/lib/format";
import { applicaPalette, PALETTE, PALETTE_DEFAULT } from "@/lib/palette";

export default function Impostazioni() {
  const [config, setConfig] = useState([]);
  const [dq, setDq] = useState(null);
  const [anag, setAnag] = useState(null);
  const [audit, setAudit] = useState([]);
  const [soglie, setSoglie] = useState({ warning: -5, critico: -15 });
  const [modalita, setModalita] = useState("calcolata");
  const [comms, setComms] = useState([]);
  const [budget, setBudget] = useState({});
  const [giorni, setGiorni] = useState({ anno: "", anno_trascorsi: "", mese: "", mese_trascorsi: "" });
  const [holidays, setHolidays] = useState([]);
  const [mesiGiorni, setMesiGiorni] = useState({});
  const [sbloccato, setSbloccato] = useState(false);
  const [calc, setCalc] = useState(null);
  const [bgtMens, setBgtMens] = useState([]);
  const [palette, setPalette] = useState({ ...PALETTE });
  const [prodotti, setProdotti] = useState({});
  const [nuovoProd, setNuovoProd] = useState({ codice: "", settore: "" });
  const ANNO = 2026;
  const CATS = [["CARICATO", "TOTALE"], ["CARICATO", "AUTO"], ["CARICATO", "POS"],
    ["CARICATO", "ASSICURAZIONI"],
    ["LIQUIDATO", "TOTALE"], ["LIQUIDATO", "AUTO"], ["LIQUIDATO", "POS"],
    ["LIQUIDATO", "ASSICURAZIONI"]];
  const bkey = (slug, tipo, cat) => `${slug || "AGENZIA"}|${tipo}|${cat}`;

  const load = () => {
    api.get("/config").then((r) => {
      setConfig(r.data.rows);
      const dsp = r.data.rows.find((c) => c.key === "dealer_senza_proiezione");
      setDealerFermi(dsp ? (dsp.value || []).join(", ") : "10917832");
      const s = r.data.rows.find((c) => c.key === "soglie_kpi");
      if (s) setSoglie(s.value);
      const prod = r.data.rows.find((x) => x.key === "prodotti_settore");
      if (prod) setProdotti(prod.value || {});
      const pal = r.data.rows.find((x) => x.key === "palette_colori");
      if (pal) { setPalette({ ...PALETTE_DEFAULT, ...pal.value }); applicaPalette(pal.value); }
      const c = r.data.rows.find((x) => x.key === "calendario");
      if (c) {
        setModalita(c.value.modalita_proiezione || "calcolata");
        setHolidays(c.value.holidays || []);
        const g = c.value.giorni || {};
        setGiorni({ anno: g.anno ?? "", anno_trascorsi: g.anno_trascorsi ?? "",
          mese: g.mese ?? "", mese_trascorsi: g.mese_trascorsi ?? "" });
        setMesiGiorni(g.mesi || {});
      }
    });
    api.get("/data-quality").then((r) => setDq(r.data)).catch(() => setDq(null));
    api.get("/anagrafiche").then((r) => setAnag(r.data)).catch(() => setAnag(null));
    api.get("/audit").then((r) => setAudit(r.data.rows)).catch(() => setAudit([]));
    api.get("/budget-premi").then((r) => setComms(r.data.commerciali)).catch(() => setComms([]));
    api.get(`/budget?anno=${ANNO}`).then((r) => {
      const m = {};
      r.data.rows.filter((x) => x.mese == null && ["CARICATO", "LIQUIDATO"].includes(x.tipo_kpi))
        .forEach((x) => { m[bkey(x.commerciale_slug, x.tipo_kpi, x.categoria)] = x.importo_budget ?? ""; });
      setBudget(m);
    }).catch(() => setBudget({}));
    api.get(`/budget-mensile?anno=${ANNO}`)
      .then((r) => setBgtMens(r.data.rows)).catch(() => setBgtMens([]));
    api.get(`/dashboard/overview?anno=${ANNO}`).then((r) => setCalc(r.data.calendario)).catch(() => setCalc(null));
  };
  useEffect(() => { load(); }, []);

  const [dealerFermi, setDealerFermi] = useState("");
  const saveDealerFermi = async () => {
    const codici = dealerFermi.split(/[,\s;]+/).map((c) => c.trim()).filter(Boolean);
    try {
      await api.put("/config", { key: "dealer_senza_proiezione", value: codici });
      toast.success("Dealer senza proiezione salvati");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveSoglie = async () => {
    try {
      await api.put("/config", { key: "soglie_kpi", value: { warning: Number(soglie.warning), critico: Number(soglie.critico) } });
      toast.success("Soglie salvate");
    } catch (e) { toast.error(apiError(e)); }
  };
  const salvaProdotti = async (valore) => {
    try {
      await api.put("/config", { key: "prodotti_settore", value: valore });
      setProdotti(valore);
      toast.success("Tabella prodotti aggiornata");
    } catch (e) { toast.error(apiError(e)); }
  };
  const aggiungiProdotto = () => {
    const c = nuovoProd.codice.trim();
    const sett = nuovoProd.settore.trim().toUpperCase();
    if (!c || !sett) { toast.error("Indica codice prodotto e settore"); return; }
    salvaProdotti({ ...prodotti, [c]: sett });
    setNuovoProd({ codice: "", settore: "" });
  };
  const rimuoviProdotto = (c) => {
    const copia = { ...prodotti };
    delete copia[c];
    salvaProdotti(copia);
  };

  const savePalette = async (valori) => {
    const v = valori || palette;
    const hex = /^#[0-9a-fA-F]{6}$/;
    if (!hex.test(v.caricato) || !hex.test(v.liquidato)) {
      toast.error("Colore non valido: usa il formato #RRGGBB");
      return;
    }
    try {
      await api.put("/config", { key: "palette_colori", value: { caricato: v.caricato, liquidato: v.liquidato } });
      applicaPalette(v);
      toast.success("Palette salvata · grafici, tabelle e PDF aggiornati");
    } catch (e) { toast.error(apiError(e)); }
  };
  const resetPalette = () => { setPalette({ ...PALETTE_DEFAULT }); savePalette(PALETTE_DEFAULT); };

  const saveCalendario = async () => {
    try {
      await api.put("/config", { key: "calendario", value: { holidays, modalita_proiezione: modalita, giorni } });
      toast.success("Configurazione calendario salvata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const MESE_CORRENTE = calc?.mese || new Date().getMonth() + 1;
  const setMeseCorrente = (campo, valore) => {
    const k = String(MESE_CORRENTE);
    setMesiGiorni({ ...mesiGiorni, [k]: { ...(mesiGiorni[k] || {}), [campo]: valore } });
  };

  const chiediSblocco = () => {
    if (sbloccato) return true;
    const ok = window.confirm(
      `I calcoli attuali confermano ${lavorativiAnno} giorni lavorativi e ${lavoratiAnno} giorni lavorati.\n\n`
      + "Vuoi modificare manualmente anche questi valori? L'unico campo normalmente modificabile è "
      + "\"Giorni lavorati (a oggi)\".");
    if (ok) setSbloccato(true);
    return ok;
  };
  const lavorativiAnno = Array.from({ length: 12 }, (_, i) =>
    Number(mesiGiorni[String(i + 1)]?.lavorativi) || 0).reduce((a, b) => a + b, 0);
  const lavoratiAnno = Array.from({ length: 12 }, (_, i) => {
    const v = mesiGiorni[String(i + 1)] || {};
    if (i + 1 < MESE_CORRENTE) return Number(v.lavorati) || Number(v.lavorativi) || 0;
    if (i + 1 === MESE_CORRENTE) return Number(v.lavorati) || 0;
    return 0;
  }).reduce((a, b) => a + b, 0);

  const precompila = async () => {
    try {
      const r = await api.post(`/calendario/precompila?anno=${ANNO}`);
      toast.success(`Calendario compilato · ${num(r.data.giorni_lavorativi_anno)} giorni lavorativi, ${num(r.data.giorni_lavorati_anno)} lavorati`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveGiorni = async () => {
    try {
      await api.put("/config", { key: "calendario", value: { holidays,
        modalita_proiezione: modalita,
        giorni: {
          anno: null,
          anno_trascorsi: null,
          mese: null,
          mese_trascorsi: null,
          mesi: Object.fromEntries(Object.entries(mesiGiorni).map(([m, v]) => [m, {
            lavorati: Number(v.lavorati) || null, lavorativi: Number(v.lavorativi) || null,
          }])),
        } } });
      toast.success("Giorni lavorativi aggiornati · proiezioni ricalcolate");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveBudget = async () => {
    const rows = [];
    [{ slug: null, nome: "Totale agenzia" }, ...comms].forEach((c) => {
      CATS.forEach(([tipo, cat]) => {
        const v = budget[bkey(c.slug, tipo, cat)];
        if (v !== undefined && v !== "") {
          rows.push({ commerciale_slug: c.slug, anno: ANNO, tipo_kpi: tipo, categoria: cat,
            importo_budget: Number(v) });
        }
      });
    });
    try {
      const r = await api.post("/budget/bulk", rows);
      toast.success(`Budget salvati · ${r.data.aggiornati} valori aggiornati`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };


  return (
    <div data-testid="impostazioni-page">
      <SectionTitle sub="Configurazioni, qualità dati e audit.">Impostazioni</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PushEventiAdmin />
        <Card className="p-6" data-testid="config-soglie">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Soglie colore KPI (scostamento %)</h3>
          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Soglia warning (ambra)</label>
              <input className={inputCls} type="number" value={soglie.warning} data-testid="soglia-warning"
                onChange={(e) => setSoglie({ ...soglie, warning: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Soglia critica (rosso)</label>
              <input className={inputCls} type="number" value={soglie.critico} data-testid="soglia-critico"
                onChange={(e) => setSoglie({ ...soglie, critico: e.target.value })} />
            </div>
            <button onClick={saveSoglie} data-testid="save-soglie"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">Salva</button>
          </div>
        </Card>

        <Card className="p-6" data-testid="config-dealer-fermi">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-2">Dealer senza proiezione</h3>
          <p className="text-xs text-slate-500 mb-3">Convenzionati che non produrranno più nulla entro fine anno (es. Progetto e Posa). Nei tachimetri e nei box budget:
            il loro <b>caricato</b> viene escluso del tutto, il loro <b>liquidato</b> entra a consuntivo senza proiezione. Codici dealer separati da virgola.</p>
          <div className="flex gap-3 items-end flex-wrap">
            <input className={`${inputCls} w-96`} value={dealerFermi} data-testid="dealer-fermi-input" placeholder="es. 10917832, 12345678"
              onChange={(e) => setDealerFermi(e.target.value)} />
            <button onClick={saveDealerFermi} data-testid="save-dealer-fermi"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">Salva</button>
          </div>
        </Card>

        <Card className="p-6" data-testid="config-calendario">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">Calendario lavorativo e proiezioni</h3>
          <p className="text-sm text-slate-500 mb-4">
            Le proiezioni usano i giorni lavorativi (lun–ven, esclusi i giorni festivi configurati).
          </p>
          <div className="flex gap-4 items-end">
            <select className={inputCls} value={modalita} onChange={(e) => setModalita(e.target.value)} data-testid="modalita-proiezione">
              <option value="calcolata">Proiezione calcolata</option>
              <option value="importata">Proiezione importata (se presente nel dataset)</option>
              <option value="confronto">Confronta entrambe</option>
            </select>
            <button onClick={saveCalendario} data-testid="save-calendario"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">Salva</button>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="p-6" data-testid="config-giorni-lavorativi">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
            Giorni lavorativi e proiezioni
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Proiezione = progressivo ÷ giorni lavorati × giorni lavorativi totali. Lascia vuoto un campo
            per usare il calcolo automatico dal calendario.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Giorni lavorati anno (a oggi)</label>
              <input className={`${inputCls} bg-slate-50`} type="number" readOnly data-testid="giorni-anno_trascorsi"
                value={lavoratiAnno} />
              <p className="text-[10px] text-slate-400 mt-1">Somma mesi conclusi + mese corrente</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Giorni lavorativi anno</label>
              <input className={`${inputCls} bg-slate-50`} type="number" readOnly data-testid="giorni-anno"
                value={lavorativiAnno} />
              <p className="text-[10px] text-slate-400 mt-1">Somma dei 12 mesi</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">
                Giorni lavorati {MESI[MESE_CORRENTE - 1]} (a oggi)
              </label>
              <input className={`${inputCls} border-slate-900`} type="number" data-testid="giorni-mese_trascorsi"
                value={mesiGiorni[String(MESE_CORRENTE)]?.lavorati ?? ""}
                onChange={(e) => setMeseCorrente("lavorati", e.target.value)}
                onBlur={saveGiorni} />
              <p className="text-[10px] text-slate-400 mt-1">Unico campo modificabile · al salvataggio ricalcola proiezioni e previsioni</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">
                Giorni lavorativi {MESI[MESE_CORRENTE - 1]}
              </label>
              <input className={`${inputCls} ${sbloccato ? "" : "bg-slate-50"}`} type="number" data-testid="giorni-mese"
                readOnly={!sbloccato} onClick={chiediSblocco}
                value={mesiGiorni[String(MESE_CORRENTE)]?.lavorativi ?? ""}
                onChange={(e) => setMeseCorrente("lavorativi", e.target.value)} />
            </div>
          </div>
          <div className="mt-6 border-t border-slate-100 pt-5">
            <h4 className="uppercase text-[11px] tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Giorni per mese · per proiezioni e previsioni mensili
              <span className="ml-3 normal-case tracking-normal text-[11px] font-normal text-slate-400" data-testid="stato-blocco">
                {sbloccato ? "modifica manuale abilitata" : "bloccati · clicca un campo per sbloccarli"}
              </span>
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {MESI.map((nome, i) => {
                const k = String(i + 1);
                const v = mesiGiorni[k] || {};
                const set = (campo) => (e) => setMesiGiorni({
                  ...mesiGiorni, [k]: { ...v, [campo]: e.target.value },
                });
                return (
                  <div key={k} className="border border-slate-200 rounded-md p-3" data-testid={`giorni-mese-${k}`}>
                    <div className="text-xs font-semibold text-slate-700 mb-2">{nome} {ANNO}</div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 block">Giorni lavorati</label>
                    <input className={`${inputCls} mb-2 ${sbloccato ? "" : "bg-slate-50"}`} type="number" value={v.lavorati ?? ""}
                      readOnly={!sbloccato} onClick={chiediSblocco}
                      data-testid={`giorni-mese-${k}-lavorati`} onChange={set("lavorati")} />
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 block">Giorni lavorativi</label>
                    <input className={`${inputCls} ${sbloccato ? "" : "bg-slate-50"}`} type="number" value={v.lavorativi ?? ""}
                      readOnly={!sbloccato} onClick={chiediSblocco}
                      data-testid={`giorni-mese-${k}-lavorativi`} onChange={set("lavorativi")} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <button onClick={precompila} data-testid="precompila-giorni"
              className="border-2 border-slate-900 text-slate-900 rounded-md px-4 py-2 text-sm hover:bg-slate-900 hover:text-white transition-colors duration-200">Compila automaticamente</button>
            <button onClick={saveGiorni} data-testid="save-giorni"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">Salva e ricalcola</button>
            {calc && (
              <span className="text-sm font-mono text-slate-500" data-testid="giorni-attuali">
                In uso: {num(calc.giorni_lavorativi_anno_trascorsi)}/{num(calc.giorni_lavorativi_anno)} giorni anno ·
                {" "}{num(calc.giorni_lavorativi_mese_trascorsi)}/{num(calc.giorni_lavorativi_mese)} giorni mese
                {calc.giorni_override ? " · valori manuali" : " · calcolo automatico"}
              </span>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="p-6 overflow-x-auto" data-testid="config-budget-annui">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
            Budget annui {ANNO} · caricato e liquidato
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Importi in euro per commerciale e per il totale agenzia, comprese le colonne dei
            premi assicurativi (CPI + CVT) che alimentano i relativi tachimetri e proiezioni.
            Lascia vuoto per non impostare il budget.
          </p>
          <table className="w-full text-sm min-w-[900px]" data-testid="budget-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <th className="text-left px-3 py-2">Soggetto</th>
                {CATS.map(([tipo, cat]) => (
                  <th key={`${tipo}${cat}`}
                    className={`text-right px-3 py-2 ${cat === "ASSICURAZIONI" ? "text-sky-600"
                      : tipo === "CARICATO" ? "txt-caricato" : "txt-liquidato"}`}>
                    {tipo === "CARICATO" ? "Caricato" : "Liquidato"}{" "}
                    {cat === "POS" ? "NON AUTO" : cat === "ASSICURAZIONI" ? "PREMI ASS." : cat}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[{ slug: null, nome: "Totale agenzia" }, ...comms].map((c) => (
                <tr key={c.slug || "agenzia"} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{c.nome}</td>
                  {CATS.map(([tipo, cat]) => (
                    <td key={`${tipo}${cat}`} className="px-2 py-2">
                      <input type="number" className={`${inputCls} text-right w-full font-mono`}
                        data-testid={`budget-${c.slug || "agenzia"}-${tipo}-${cat}`}
                        value={budget[bkey(c.slug, tipo, cat)] ?? ""}
                        onChange={(e) => setBudget({ ...budget, [bkey(c.slug, tipo, cat)]: e.target.value })} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4">
            <button onClick={saveBudget} data-testid="save-budget-annui"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">Salva budget annui</button>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="p-6 overflow-x-auto" data-testid="config-budget-mensile">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
            Budget monitoraggio {ANNO} · mese per mese
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Budget assegnato dalla banca per il liquidato: AUTO e NON AUTO (BGT POS).
            Il totale è calcolato come somma delle due colonne e non è modificabile.
          </p>
          <table className="w-full text-sm min-w-[560px]" data-testid="budget-mensile-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <th className="text-left px-3 py-2">Mese</th>
                <th className="text-right px-3 py-2 text-red-600">BGT AUTO</th>
                <th className="text-right px-3 py-2 text-amber-600">BGT POS (non auto)</th>
                <th className="text-right px-3 py-2">Totale</th>
              </tr>
            </thead>
            <tbody>
              {bgtMens.map((r, i) => (
                <tr key={r.mese} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{MESI[r.mese - 1]}</td>
                  {["auto", "pos"].map((k) => (
                    <td key={k} className="px-2 py-2">
                      <input type="number" step="0.01" className={`${inputCls} text-right w-full font-mono`}
                        data-testid={`budget-mensile-${k}-${r.mese}`} value={r[k] ?? ""}
                        onChange={(e) => setBgtMens(bgtMens.map((x) => x.mese === r.mese
                          ? { ...x, [k]: e.target.value === "" ? 0 : Number(e.target.value) } : x))} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-mono font-semibold whitespace-nowrap"
                    data-testid={`budget-mensile-totale-${r.mese}`}>
                    {num((Number(r.auto) || 0) + (Number(r.pos) || 0), 2)} €
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
                <td className="px-3 py-2">Totale {ANNO}</td>
                {["auto", "pos"].map((k) => (
                  <td key={k} className="px-3 py-2 text-right font-mono"
                    data-testid={`budget-mensile-somma-${k}`}>
                    {num(bgtMens.reduce((t, r) => t + (Number(r[k]) || 0), 0), 2)} €
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-mono" data-testid="budget-mensile-somma-totale">
                  {num(bgtMens.reduce((t, r) => t + (Number(r.auto) || 0) + (Number(r.pos) || 0), 0), 2)} €
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-4">
            <button data-testid="save-budget-mensile"
              onClick={async () => {
                try {
                  await api.put(`/budget-mensile?anno=${ANNO}`, bgtMens.map((r) => ({
                    mese: r.mese, auto: Number(r.auto) || 0, pos: Number(r.pos) || 0 })));
                  toast.success("Budget monitoraggio salvato");
                } catch (e) {
                  toast.error(apiError(e));
                }
              }}
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">
              Salva budget monitoraggio
            </button>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="p-6" data-testid="config-prodotti">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
            Tabella prodotti · settore
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Serve per gli estratti che non hanno la colonna settore (es. ELENCOPRA): il settore
            viene ricavato dal codice prodotto. I codici non elencati usano la corrispondenza
            appresa dallo storico. I codici prodotto <b>01, 02 e 03</b> sono classificati
            <b> FUORI PERIMETRO</b>: restano fuori sia da AUTO sia da NON AUTO (POS).
          </p>
          <div className="flex flex-wrap gap-2 items-end mb-4">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Codice prodotto</label>
              <input className={`${inputCls} w-32`} value={nuovoProd.codice} data-testid="prod-codice"
                onChange={(e) => setNuovoProd({ ...nuovoProd, codice: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Settore</label>
              <input className={`${inputCls} w-48`} value={nuovoProd.settore} data-testid="prod-settore"
                placeholder="AUTO, MOTO, CASA…"
                onChange={(e) => setNuovoProd({ ...nuovoProd, settore: e.target.value })} />
            </div>
            <button onClick={aggiungiProdotto} data-testid="prod-aggiungi"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800
                transition-colors duration-200">Aggiungi / aggiorna</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(prodotti).sort().map(([c, sett]) => (
              <span key={c} data-testid={`prod-row-${c}`}
                className="inline-flex items-center gap-2 border border-slate-200 rounded-md px-2 py-1 text-sm">
                <span className="font-mono">{c}</span> → <b>{sett}</b>
                <button onClick={() => rimuoviProdotto(c)} data-testid={`prod-elimina-${c}`}
                  className="text-slate-400 hover:text-red-600 transition-colors duration-200">×</button>
              </span>
            ))}
            {!Object.keys(prodotti).length && (
              <span className="text-sm text-slate-400">Nessuna corrispondenza impostata a mano</span>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="p-6" data-testid="config-palette">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
            Palette colori · caricato e liquidato
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            I colori scelti vengono applicati a tutti i grafici, tachimetri, tabelle e ai report PDF.
          </p>
          <div className="flex flex-wrap items-end gap-6">
            {[["caricato", "Caricato"], ["liquidato", "Liquidato"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs text-slate-500 block mb-1">{label}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={palette[k]} data-testid={`palette-${k}`}
                    onChange={(e) => setPalette({ ...palette, [k]: e.target.value })}
                    className="h-10 w-14 rounded-md border border-slate-200 bg-white p-1 cursor-pointer" />
                  <input className={`${inputCls} w-32 font-mono uppercase`} value={palette[k]}
                    data-testid={`palette-${k}-hex`}
                    onChange={(e) => setPalette({ ...palette, [k]: e.target.value })} />
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3">
              {[["caricato", "Caricato"], ["liquidato", "Liquidato"]].map(([k, label]) => (
                <span key={k} className="rounded-md px-3 py-2 text-xs font-semibold text-white"
                  data-testid={`palette-preview-${k}`} style={{ background: palette[k] }}>{label}</span>
              ))}
            </div>
            <button onClick={() => savePalette()} data-testid="save-palette"
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">
              Salva palette
            </button>
            <button onClick={resetPalette} data-testid="reset-palette"
              className="border-2 border-slate-900 text-slate-900 rounded-md px-4 py-2 text-sm hover:bg-slate-900 hover:text-white transition-colors duration-200">
              Ripristina predefiniti
            </button>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <StrutturaMenu />
      </div>

      <div className="mt-10">
        <SectionTitle sub="Le dashboard sono affidabili solo se i dati sono corretti.">Qualità dati</SectionTitle>
        {dq && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {dq.metriche.map((m) => (
                <Card key={m.chiave} className="p-6" data-testid={`dq-${m.chiave}`}>
                  <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">{m.label}</div>
                  <div className={`font-mono text-2xl font-semibold mt-3 ${m.informativa ? "text-slate-600"
                    : m.valore > 0 ? "text-amber-600" : "text-emerald-600"}`}>{num(m.valore)}</div>
                </Card>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <Card className="p-6" data-testid="dq-stati">
                <h4 className="text-sm font-semibold mb-3">Stati da mappare</h4>
                {dq.stati_da_mappare.length === 0 ? <p className="text-sm text-slate-500">Tutti gli stati sono riconosciuti.</p> :
                  <ul className="text-sm space-y-1">{dq.stati_da_mappare.map((s, i) => (
                    <li key={i} className="flex justify-between border-b border-slate-100 py-1"><span>{s.stato || "(vuoto)"}</span><span className="font-mono">{num(s.occorrenze)}</span></li>
                  ))}</ul>}
              </Card>
              <Card className="p-6" data-testid="dq-dealer-non-censiti">
                <h4 className="text-sm font-semibold mb-3">Dealer con pratiche ma non in anagrafica</h4>
                <div className="max-h-56 overflow-y-auto">
                  <ul className="text-sm space-y-1">{dq.dealer_non_censiti.map((s, i) => (
                    <li key={i} className="flex justify-between border-b border-slate-100 py-1"><span className="font-mono">{s.codice || "(vuoto)"}</span><span className="font-mono">{num(s.pratiche)}</span></li>
                  ))}</ul>
                </div>
              </Card>
              <Card className="p-6" data-testid="dq-duplicati">
                <h4 className="text-sm font-semibold mb-3">Dealer duplicati (stessa ragione sociale)</h4>
                <div className="max-h-56 overflow-y-auto">
                  <ul className="text-sm space-y-1">{dq.dealer_duplicati.map((s, i) => (
                    <li key={i} className="border-b border-slate-100 py-1"><div>{s.ragione_sociale}</div><div className="text-xs font-mono text-slate-500">{s.codici.join(", ")}</div></li>
                  ))}</ul>
                  {dq.dealer_duplicati.length === 0 && <p className="text-sm text-slate-500">Nessun duplicato.</p>}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>

      {anag && (
        <div className="mt-10">
          <SectionTitle sub="Settori, prodotti e stati rilevati automaticamente dai dati importati.">Anagrafiche derivate</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[["Settori", anag.settori], ["Prodotti", anag.prodotti], ["Stati sorgente", anag.stati]].map(([t, rows]) => (
              <Card key={t} className="p-6" data-testid={`anag-${t}`}>
                <h4 className="text-sm font-semibold mb-3">{t}</h4>
                <div className="max-h-64 overflow-y-auto">
                  <ul className="text-sm space-y-1">{rows.map((r, i) => (
                    <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{r.key ?? "(vuoto)"}</span><span className="font-mono">{num(r.pratiche_caricate)}</span>
                    </li>
                  ))}</ul>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <ManutenzioneDb />

      <div className="mt-10">
        <SectionTitle>Audit log</SectionTitle>
        <Card className="overflow-x-auto max-h-96">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <th className="text-left px-6 py-3">Data</th><th className="text-left px-6 py-3">Azione</th>
              <th className="text-left px-6 py-3">Utente</th><th className="text-left px-6 py-3">Dettaglio</th>
            </tr></thead>
            <tbody>
              {audit.map((a, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-6 py-2 font-mono text-slate-500">{dateIt(a.at)}</td>
                  <td className="px-6 py-2">{a.action}</td>
                  <td className="px-6 py-2 text-slate-600">{a.user_email}</td>
                  <td className="px-6 py-2 text-xs text-slate-500">{a.detail ? JSON.stringify(a.detail) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
